// src/auth/authSession.ts
export type SessionStatus = "anon" | "needs-mfa" | "authed";

export type Session = {
  userId: string;
  status: SessionStatus;
  hotelId?: string | null;
};

const LS_SESSION = "xentros.session.v1";
const LS_JWT = "xentros.jwt.v1";

export const SESSION_EXPIRED_EVENT = "xentros-session-expired";

export function dispatchSessionExpired() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

export const PERMISSION_DENIED_EVENT = "xentros-permission-denied";

export function dispatchPermissionDenied(message?: string) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail: { message } }));
  }
}

export function getSession(): Session | null {
  const raw = localStorage.getItem(LS_SESSION);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function setSession(session: Session) {
  localStorage.setItem(LS_SESSION, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(LS_SESSION);
  clearJwtToken();
}

// ─── JWT token storage ────────────────────────────────────────────────────────
// The JWT lives in memory only, so injected script cannot read it from storage
// (XEN-91). After a reload it is re-issued by /api/auth/me from the httpOnly
// session cookie (restoreJwtFromSessionCookie in backendAuth.ts).

let memoryJwt: string | null = null;

// Tokens written by builds before XEN-91 must not linger in storage.
try {
  localStorage.removeItem(LS_JWT);
} catch {
  /* storage unavailable */
}

// Logout in another tab clears the shared session record; drop this tab's token too.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if ((event.key === LS_SESSION || event.key === null) && event.newValue === null && memoryJwt) {
      memoryJwt = null;
      dispatchSessionExpired();
    }
  });
}

export function getJwtToken(): string | null {
  return memoryJwt;
}

export function setJwtToken(token: string) {
  memoryJwt = token;
}

export function clearJwtToken() {
  memoryJwt = null;
}

export function isJwtTokenExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return Math.floor(Date.now() / 1000) >= payload.exp;
  } catch {
    return true;
  }
}

export function getValidJwtToken(): string | null {
  const token = getJwtToken();
  if (!token) return null;
  if (isJwtTokenExpired(token)) {
    clearJwtToken();
    return null;
  }
  return token;
}
